import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/toaster";

import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import Features from "./pages/Features";
import Customers from "./pages/Customers";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import AcceptInvite from "./pages/AcceptInvite";
import SsoLanding from "./pages/SsoLanding";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import VerifyEmail from "./pages/VerifyEmail";
import ResetPassword from "./pages/ResetPassword";

import AppShell from "./components/app/AppShell";
import Dashboard from "./pages/app/Dashboard";
import TimeTracker from "./pages/app/TimeTracker";
import Projects from "./pages/app/Projects";
import ProjectDetail from "./pages/app/ProjectDetail";
import Clients from "./pages/app/Clients";
import Invoices from "./pages/app/Invoices";
import InvoiceNew from "./pages/app/InvoiceNew";
import Reports from "./pages/app/Reports";
import Members from "./pages/app/Members";
import RateCards from "./pages/app/RateCards";
import Planning from "./pages/app/Planning";
import Settings from "./pages/app/Settings";
import Account from "./pages/app/Account";
import Tasks from "./pages/app/Tasks";
import ImportHub from "./pages/app/Import";
import XeroCallback from "./pages/XeroCallback";

import StaffConsole from "./pages/staff/StaffConsole";
import BenchTeam from "./pages/staff/BenchTeam";

import CompareHub from "./pages/compare/CompareHub";
import CompareDetail from "./pages/compare/CompareDetail";

function RequireAuth({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/signin" replace />;
  return children;
}

function RequireStaff({ children, adminOnly = false }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/signin" replace />;
  const role = user.platform_role || "none";
  const isStaff = ["readonly", "support", "admin"].includes(role);
  if (!isStaff) return <Navigate to="/app" replace />;
  if (adminOnly && role !== "admin") return <Navigate to="/staff" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/sso" element={<SsoLanding />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/xero/callback" element={<XeroCallback />} />

            <Route path="/compare" element={<CompareHub />} />
            <Route path="/compare/:slug" element={<CompareDetail />} />

            <Route path="/app" element={<RequireAuth><AppShell /></RequireAuth>}>
              <Route index element={<Dashboard />} />
              <Route path="time" element={<TimeTracker />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/:id" element={<ProjectDetail />} />
              <Route path="clients" element={<Clients />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="invoices/new" element={<InvoiceNew />} />
              <Route path="reports" element={<Reports />} />
              <Route path="reports/mine" element={<Reports mine />} />
              <Route path="rate-cards" element={<RateCards />} />
              <Route path="planning" element={<Planning />} />
              <Route path="settings" element={<Settings />} />
              <Route path="account" element={<Account />} />
              <Route path="members" element={<Members />} />
              <Route path="import" element={<ImportHub />} />
            </Route>

            <Route path="/staff" element={<RequireStaff><StaffConsole /></RequireStaff>} />
            <Route path="/staff/team" element={<RequireStaff adminOnly><BenchTeam /></RequireStaff>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </div>
  );
}

export default App;
